import axios from "./config";
import { getHeader } from "../utility/index";

// Interface for coordinate
export interface Coordinate {
  lat: number;
  lng: number;
}

// Interface for creating a territory (POST request)
export interface CreateTerritoryPayload {
  comment: string;
  coordinates: Coordinate[];
  territory_name: string;
}

// Interface for updating a territory (PUT request)
export interface UpdateTerritoryPayload {
  territory_name: string;
  coordinates: Coordinate[];
  comment: string;
}

// Interface for territory response
export interface TerritoryResponse {
  id?: string | number;
  territory_name: string;
  coordinates: Coordinate[];
  comment: string;
  [key: string]: any;
}

// Interface for list response
export interface TerritoryListResponse {
  data?: TerritoryResponse[];
  items?: TerritoryResponse[];
  results?: TerritoryResponse[];
  [key: string]: any;
}

/**
 * Get all LMD territories
 * GET: /admin/lmd-territories
 */
export const getAllLmdTerritories = async (salesOfficeId?: string): Promise<TerritoryResponse[]> => {
  let url = `/admin/lmd-territories`;
  if (salesOfficeId) {
    url += `?sales_office_id=${salesOfficeId}`;
  }
  const response = await axios.get<TerritoryListResponse>(url, {
    headers: getHeader(),
  });
  
  // Log the raw response for debugging
  console.log("Raw API response:", response.data);
  
  // Handle different response structures
  const data = response.data;
  
  // Check if territories exists and is an array
  if (data?.territories && Array.isArray(data.territories)) {
    console.log("Returning", data.territories.length, "territories from data.territories");
    return data.territories;
  }
  
  // Fallback: try other possible structures
  if (Array.isArray(data)) {
    console.log("Response is direct array, returning", data.length, "territories");
    return data;
  } else if (data?.data && Array.isArray(data.data)) {
    console.log("Response has data.data array, returning", data.data.length, "territories");
    return data.data;
  } else if (data?.items && Array.isArray(data.items)) {
    console.log("Response has data.items array, returning", data.items.length, "territories");
    return data.items;
  } else if (data?.results && Array.isArray(data.results)) {
    console.log("Response has data.results array, returning", data.results.length, "territories");
    return data.results;
  }
  
  console.warn("Unexpected response structure:", data);
  console.warn("Response keys:", Object.keys(data || {}));
  return [];
};

/**
 * Create a new LMD territory
 * POST: /web/api/v1/admin/lmd-territories
 */
export const createLmdTerritory = async (
  payload: CreateTerritoryPayload
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories`;
  const response = await axios.post<TerritoryResponse>(url, payload, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Get a specific LMD territory by ID
 * GET: /web/api/v1/admin/lmd-territories/{territory_id}
 */
export const getLmdTerritory = async (
  territoryId: string | number
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  const response = await axios.get<TerritoryResponse>(url, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Update an existing LMD territory
 * PUT: /web/api/v1/admin/lmd-territories/{territory_id}
 */
export const updateLmdTerritory = async (
  territoryId: string | number,
  payload: UpdateTerritoryPayload
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  const response = await axios.put<TerritoryResponse>(url, payload, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Delete an LMD territory
 * DELETE: /admin/lmd-territories/{territory_id}
 */
export const deleteLmdTerritory = async (
  territoryId: string | number
): Promise<void> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  await axios.delete(url, {
    headers: getHeader(),
  });
};

